/**
 * Centralized payment service. Every "Pay Now" button funnels through this.
 * Adding a new gateway later (Stripe, PayPal) is a new export in this file — no UI change.
 */
const API = process.env.REACT_APP_BACKEND_URL;

let _configCache = null;
let _scriptLoading = null;

/** GET /api/payments/config — cached. */
export async function getPaymentConfig() {
  if (_configCache) return _configCache;
  const res = await fetch(`${API}/api/payments/config`);
  if (!res.ok) throw new Error('Payment configuration unavailable');
  _configCache = await res.json();
  return _configCache;
}

/** Load the Razorpay Checkout script once. */
export function loadRazorpayScript() {
  if (typeof window !== 'undefined' && window.Razorpay) return Promise.resolve(true);
  if (_scriptLoading) return _scriptLoading;
  _scriptLoading = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => reject(new Error('Failed to load Razorpay Checkout'));
    document.body.appendChild(s);
  });
  return _scriptLoading;
}

/**
 * POST /api/payments/create-order
 * @returns { order_id, amount_minor, amount_display, currency, rate, receipt }
 */
export async function createOrder(payload) {
  const res = await fetch(`${API}/api/payments/create-order`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const text = await res.text();
  let data = {};
  try { data = text ? JSON.parse(text) : {}; } catch { data = { detail: text }; }
  if (!res.ok) throw new Error(data.detail || 'Could not create order');
  return data;
}

/** POST /api/payments/verify */
export async function verifyPayment(payload) {
  const res = await fetch(`${API}/api/payments/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const text = await res.text();
  let data = {};
  try { data = text ? JSON.parse(text) : {}; } catch { data = { detail: text }; }
  if (!res.ok) throw new Error(data.detail || 'Payment verification failed');
  return data;
}

/** POST /api/payments/create-subscription */
export async function createSubscription(payload) {
  const res = await fetch(`${API}/api/payments/create-subscription`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const text = await res.text();
  let data = {};
  try { data = text ? JSON.parse(text) : {}; } catch { data = { detail: text }; }
  if (!res.ok) throw new Error(data.detail || 'Could not create subscription');
  return data;
}

/**
 * Full one-time checkout flow.
 *   opts = {
 *     amountEur, currency, product, label, name, email,
 *     onSuccess({ order_id, payment_id, signature, amount_display, currency }),
 *     onFailure(error),
 *   }
 */
export async function payWithRazorpay(opts) {
  const {
    amountEur, currency, product, label, name = '', email = '',
    onSuccess, onFailure, theme = { color: '#1A2F4A' }, description,
  } = opts;

  await loadRazorpayScript();
  const config = await getPaymentConfig();
  if (!config.razorpay_key_id) throw new Error('Payment gateway is not configured');

  const order = await createOrder({
    amount_eur: amountEur,
    currency,
    product,
    label,
    customer_email: email || undefined,
    customer_name: name || undefined,
  });

  return new Promise((resolve, reject) => {
    const rzp = new window.Razorpay({
      key: config.razorpay_key_id,
      amount: order.amount_minor,
      currency: order.currency,
      order_id: order.order_id,
      name: 'Selora French Academy',
      description: description || label || product,
      prefill: { name, email },
      theme,
      handler: async (response) => {
        try {
          await verifyPayment({
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          });
          const result = {
            order_id: response.razorpay_order_id,
            payment_id: response.razorpay_payment_id,
            signature: response.razorpay_signature,
            amount_display: order.amount_display,
            currency: order.currency,
            product,
            label,
          };
          if (onSuccess) onSuccess(result);
          resolve(result);
        } catch (err) {
          if (onFailure) onFailure(err);
          reject(err);
        }
      },
      modal: {
        ondismiss: () => {
          const err = new Error('Payment cancelled');
          if (onFailure) onFailure(err);
          reject(err);
        },
      },
    });
    rzp.on('payment.failed', (resp) => {
      const err = new Error(resp?.error?.description || 'Payment failed');
      if (onFailure) onFailure(err);
      reject(err);
    });
    rzp.open();
  });
}
